import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../components/layout/PublicLayout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Mail, Phone, MessageSquare } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Contact() {
  const { user } = useAuth();
  const { settings } = useCurrency();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(!!user);

  useEffect(() => {
    if (!user) return;
    api.get("/support/tickets/mine")
      .then(({ data }) => setTickets(data))
      .finally(() => setLoadingTickets(false));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/support/tickets", form);
      toast.success("Your message has been sent — we'll get back to you soon.");
      if (user) {
        setForm({ ...form, subject: "", message: "" });
        const { data: fresh } = await api.get("/support/tickets/mine");
        setTickets(fresh);
      } else {
        navigate(`/support/ticket/${data.id}?token=${data.guest_token}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-[10px] uppercase tracking-[0.2em] text-accent font-bold mb-3">Support</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-3">Contact Us / Report an Issue</h1>
        <p className="text-sm text-muted-foreground mb-8">
          Have a question, an issue with a booking, or feedback for us? Send a message below and our team will get back to you.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <Card className="md:col-span-8 border-border p-6">
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Full name</Label>
                  <Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email</Label>
                  <Input type="email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Phone (optional)</Label>
                <Input className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Subject</Label>
                <Input className="mt-1.5" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Message</Label>
                <Textarea rows={6} className="mt-1.5" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="contact-submit-btn">
                {submitting ? "Sending…" : "Send message"}
              </Button>
            </form>
          </Card>

          <div className="md:col-span-4 space-y-4">
            <Card className="border-border p-5">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Reach us directly</div>
              {settings?.platform_contact?.email && (
                <a href={`mailto:${settings.platform_contact.email}`} className="flex items-center gap-2 text-sm hover:text-primary transition-colors mb-2">
                  <Mail size={14} /> {settings.platform_contact.email}
                </a>
              )}
              {settings?.platform_contact?.phone && (
                <a href={`tel:${settings.platform_contact.phone}`} className="flex items-center gap-2 text-sm hover:text-primary transition-colors">
                  <Phone size={14} /> {settings.platform_contact.phone}
                </a>
              )}
            </Card>
          </div>
        </div>

        {user && (
          <div className="mt-12">
            <h2 className="font-display text-xl font-semibold tracking-tight mb-4">Your messages</h2>
            {loadingTickets ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : tickets.length === 0 ? (
              <Card className="border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                <MessageSquare size={22} className="mx-auto mb-2 text-muted-foreground" />
                You haven't sent any messages yet.
              </Card>
            ) : (
              <div className="space-y-3">
                {tickets.map((t) => (
                  <Link key={t.id} to={`/support/ticket/${t.id}`}>
                    <Card className="border-border p-4 hover:shadow-organic transition-shadow flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-display font-semibold truncate">{t.subject}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{new Date(t.updated_at).toLocaleString()} · {t.messages.length} message{t.messages.length > 1 ? "s" : ""}</div>
                      </div>
                      <Badge variant={t.status === "open" ? "default" : "outline"} className="capitalize shrink-0">{t.status}</Badge>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
